/**
 * Data-asset contracts (spec §5b.16). These formats are the seam between hand-editing (asset studios)
 * and AI generation: the model writes code that CONSUMES them, humans edit the data. Neither
 * overwrites the other.
 */
import { z } from 'zod';

/* --------------------------------------- sprite atlas --------------------------------------- */

export const FrameRectSchema = z.object({
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
});
export type FrameRect = z.infer<typeof FrameRectSchema>;

export const AnimationSchema = z.object({
  name: z.string().min(1).max(64),
  frames: z.array(z.number().int().min(0)).min(1),
  fps: z.number().min(1).max(60).default(12),
  loop: z.boolean().default(true),
});
export type Animation = z.infer<typeof AnimationSchema>;

/** Sprite-sheet atlas — produced by the sprite editor, consumed by generated game code. */
export const AtlasSchema = z.object({
  version: z.literal(1),
  image: z.string().min(1),
  frameWidth: z.number().int().positive(),
  frameHeight: z.number().int().positive(),
  /** columns x rows of the canonical grid the base sprite defines. */
  cols: z.number().int().positive(),
  rows: z.number().int().positive(),
  frames: z.array(FrameRectSchema),
  animations: z.array(AnimationSchema),
  /** perspective the base was drawn in — generation must preserve it. */
  view: z.enum(['flat', 'isometric', 'top-down', 'side', 'pixel']),
});
export type Atlas = z.infer<typeof AtlasSchema>;

/* ----------------------------------------- colliders ---------------------------------------- */

export const Collider2DSchema = z.discriminatedUnion('shape', [
  z.object({
    shape: z.literal('box'),
    role: z.enum(['hit', 'hurt', 'trigger']).default('hit'),
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
  }),
  z.object({
    shape: z.literal('circle'),
    role: z.enum(['hit', 'hurt', 'trigger']).default('hit'),
    x: z.number(),
    y: z.number(),
    r: z.number().positive(),
  }),
  z.object({
    shape: z.literal('polygon'),
    role: z.enum(['hit', 'hurt', 'trigger']).default('hit'),
    points: z.array(z.object({ x: z.number(), y: z.number() })).min(3),
  }),
]);
export type Collider2D = z.infer<typeof Collider2DSchema>;

/** Per-frame 2D colliders — base sprites ship with presets; the hitbox editor refines them. */
export const ColliderSet2DSchema = z.object({
  version: z.literal(1),
  dimension: z.literal('2d'),
  /** index into Atlas.frames; -1 = applies to every frame. */
  perFrame: z.array(z.object({ frame: z.number().int().min(-1), colliders: z.array(Collider2DSchema) })),
});
export type ColliderSet2D = z.infer<typeof ColliderSet2DSchema>;

export const Collider3DSchema = z.discriminatedUnion('shape', [
  z.object({
    shape: z.literal('box'),
    role: z.enum(['hit', 'hurt', 'trigger']).default('hit'),
    center: z.tuple([z.number(), z.number(), z.number()]),
    size: z.tuple([z.number().positive(), z.number().positive(), z.number().positive()]),
    rotation: z.tuple([z.number(), z.number(), z.number()]).optional(),
  }),
  z.object({
    shape: z.literal('sphere'),
    role: z.enum(['hit', 'hurt', 'trigger']).default('hit'),
    center: z.tuple([z.number(), z.number(), z.number()]),
    radius: z.number().positive(),
  }),
  z.object({
    shape: z.literal('capsule'),
    role: z.enum(['hit', 'hurt', 'trigger']).default('hit'),
    center: z.tuple([z.number(), z.number(), z.number()]),
    radius: z.number().positive(),
    height: z.number().positive(),
    axis: z.enum(['x', 'y', 'z']).default('y'),
  }),
]);
export type Collider3D = z.infer<typeof Collider3DSchema>;

export const ColliderSet3DSchema = z.object({
  version: z.literal(1),
  dimension: z.literal('3d'),
  model: z.string().min(1),
  colliders: z.array(Collider3DSchema),
});
export type ColliderSet3D = z.infer<typeof ColliderSet3DSchema>;

/* ------------------------------------- entity schemas --------------------------------------- */

/**
 * Access rules are evaluated SERVER-SIDE (spec D13/§10.2 layer 4) — this is what makes generated
 * leaderboards cheat-resistant. `owner` = the app-scoped user who created the row.
 */
export const EntityRuleSchema = z.object({
  read: z.enum(['public', 'owner', 'room', 'none']).default('public'),
  write: z.enum(['public', 'owner', 'room', 'none']).default('owner'),
  delete: z.enum(['public', 'owner', 'room', 'none']).default('owner'),
});
export type EntityRule = z.infer<typeof EntityRuleSchema>;

export const EntityDefinitionSchema = z.object({
  version: z.literal(1),
  name: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'entity names are lower_snake_case'),
  /** advisory JSON Schema — validating, never migration-bound (schemaless evolution, Base44 parity). */
  jsonSchema: z.record(z.unknown()),
  rules: EntityRuleSchema,
});
export type EntityDefinition = z.infer<typeof EntityDefinitionSchema>;
